import React, { useContext, useEffect, useState } from 'react';
import { useApp } from '../../context/app-context';
import PreferencesComponent from '../Preferences/PreferencesComponent';
import { PreferencesContext } from '../Preferences/PreferencesContext';
import zipcodeToCity from '../../service/zipcode-to-city-service';
import postValidateAddress from '../../service/validate-address-service';
import storesByAddressService, { Address } from '../../service/stores-by-address-service';
import storeDetailsService from '../../service/store-details-service';
import saveAddressService, { AddressDataType } from '../../service/save-address-service';
import deleteAddressService from '../../service/delete-address-service';
import { get, invoke, isEmpty } from 'lodash';
import getSlotsInfo from '../../service/get-slots-service';
import getAllAddresses from '../../service/get-all-addresses-service';
import { SessionContext, SessionContextType } from './SessioncContext';
import PropTypes from 'prop-types';

const ThirdTabContent = (props) => {
  const getPreferences = get(props, 'getPreferences');
  const getUpcomingOrders = get(props, 'getUpcomingOrders');
  const preferencesData = get(props, 'preferencesData');
  const upcomingOrders = get(props, 'upcomingOrders');
  const currentSessionContext = useContext(SessionContext) as SessionContextType;
  const user = get(currentSessionContext, 'user');
  const { appData, globalBannerMessage, showToast } = useApp();
  const [slotsInfo, setSlotsInfo] = useState({});
  const [isAnalyticsDataReady, setIsAnalyticsDataReady] = useState(false);

  useEffect(() => {
    if (isAnalyticsDataReady) {
      analyticsEvents('preferences-tab');
    }
  }, [isAnalyticsDataReady]);

  const analyticsEvents = (page) => {
    try {
      if (window['AB']?.DATALAYER) {
        const preferenceTabMessage = globalBannerMessage ? globalBannerMessage : '';
        window['AB'].DATALAYER.SNSInit(page, { preferenceTabMessage });
      }
    } catch (error) {
      // Handle any errors that occur during execution
      console.error('@AnalyticsTeam error:', error);
    }
  };

  const fetchPreferences = async () => {
    return getPreferences();
  };

  const fetchUpcomingOrders = async () => {
    return getUpcomingOrders();
  };

  const fetchAllAddresses = async (): Promise<any> => {
    return await invoke(currentSessionContext, 'ensureActiveSession', () =>
      getAllAddresses(appData.environmentVariables, user),
    );
  };

  const fetchCityFromZipCode = async (zipcode: string): Promise<any> => {
    return await invoke(currentSessionContext, 'ensureActiveSession', () =>
      zipcodeToCity(appData.environmentVariables, user, zipcode),
    );
  };

  const validateAddress = async (address: any): Promise<any> => {
    return await invoke(currentSessionContext, 'ensureActiveSession', () =>
      postValidateAddress(appData.environmentVariables, user, address),
    );
  };

  const storeZipCodeLookup = async (address: Address): Promise<any> => {
    return await invoke(currentSessionContext, 'ensureActiveSession', () =>
      storesByAddressService(appData.environmentVariables, user, address),
    );
  };

  const getStoreDetails = async (storeId: string): Promise<any> => {
    return await invoke(currentSessionContext, 'ensureActiveSession', () =>
      storeDetailsService(appData.environmentVariables, user, storeId),
    );
  };

  const saveAddress = async (addressData: AddressDataType): Promise<any> => {
    return await invoke(currentSessionContext, 'ensureActiveSession', () =>
      saveAddressService(appData.environmentVariables, user, addressData),
    );
  };

  const deleteAddress = async (addressId: string): Promise<any> => {
    return await invoke(currentSessionContext, 'ensureActiveSession', () =>
      deleteAddressService(appData.environmentVariables, user, addressId),
    );
  };

  const refreshTabContent = async (): Promise<void> => {
    await fetchPreferences();
    setIsAnalyticsDataReady(true);
  };

  const fetchSlotsInfo = async (): Promise<void> => {
    setSlotsInfo({ loading: true });

    try {
      const isDelivery = get(preferencesData, 'fulfillmentTypes[0].value', '').toUpperCase() !== 'DUG';
      const storeID = isDelivery
        ? get(preferencesData, 'addresses.0.stores.0.storeId', '')
        : get(preferencesData, 'stores.0.storeId', '');

      if (!isEmpty(storeID)) {
        const resp = await invoke(currentSessionContext, 'ensureActiveSession', () =>
          getSlotsInfo(appData.environmentVariables, user, storeID),
        );
        setSlotsInfo({ data: resp.data, loading: false });
      } else {
        setSlotsInfo({ error: true, loading: false });
      }
    } catch (err) {
      setSlotsInfo({ error: err, loading: false });
    }
  };

  const updateSlotsInfo = async (slotsInfo: any): Promise<any> => {
    const isDelivery = get(preferencesData, 'stores.0.fulfillmentType') === 'DELIVERY';
    const info: AddressDataType = isDelivery
      ? {
          guid: user.guid,
          address: {
            addressId: get(preferencesData, 'addresses.0.addressId', ''),
            address1: get(preferencesData, 'addresses.0.address1', ''),
            address2: get(preferencesData, 'addresses.0.address2', ''),
            addressType: get(preferencesData, 'addresses.0.addressType', ''),
            city: get(preferencesData, 'addresses.0.city', ''),
            notes: [
              {
                type: 'DELIVERY_INSTRUCTIONS',
              },
              {
                type: 'DELIVERY_TYPE',
                value: 'UNATTENDED',
              },
            ],
            state: get(preferencesData, 'addresses.0.state', ''),
            zipCode: get(preferencesData, 'addresses.0.zipCode', ''),
          },
          fulfillmentType: {
            preferenceDefId: '',
            value: get(preferencesData, 'stores.0.fulfillmentType', ''),
          },
          slotPreference: {
            slotId: get(slotsInfo, 'slotId', ''),
            fulfillmentDay: get(slotsInfo, 'fulfillmentDay', ''),
            slotStartTS: get(slotsInfo, 'slotStartTS', ''),
            slotEndTS: get(slotsInfo, 'slotEndTS', ''),
            slotType: get(slotsInfo, 'slotType', ''),
            preferenceDefId: '',
          },
        }
      : {
          guid: user.guid,
          slotPreference: {
            preferenceDefId: '',
            fulfillmentDay: get(slotsInfo, 'fulfillmentDay', ''),
            slotEndTS: get(slotsInfo, 'slotEndTS', ''),
            slotId: get(slotsInfo, 'slotId', ''),
            slotStartTS: get(slotsInfo, 'slotStartTS', ''),
            slotType: get(slotsInfo, 'slotType', ''),
          },
        };

    let resp = {};
    try {
      resp = await invoke(currentSessionContext, 'ensureActiveSession', () =>
        saveAddressService(appData.environmentVariables, user, info),
      );

      showToast('info', 'Day and time preferences successfully updated.');
    } catch (err) {}

    return { error: get(resp, 'status') !== 200 };
  };

  useEffect(() => {
    if (user && user.authToken) {
      refreshTabContent();
    }
  }, [user]);

  return (
    <PreferencesContext.Provider
      value={{
        fetchPreferences,
        fetchUpcomingOrders,
        fetchAllAddresses,
        fetchCityFromZipCode,
        validateAddress,
        storeZipCodeLookup,
        getStoreDetails,
        saveAddress,
        deleteAddress,
        slotsInfo,
        fetchSlotsInfo,
        updateSlotsInfo,
        preferencesData,
      }}
    >
      <PreferencesComponent preferences={preferencesData} upcomingOrders={upcomingOrders} />
    </PreferencesContext.Provider>
  );
};

ThirdTabContent.propTypes = {
  getPreferences: PropTypes.func,
  getUpcomingOrders: PropTypes.func,
  preferencesData: PropTypes.object.isRequired,
  upcomingOrders: PropTypes.object.isRequired,
};

export default ThirdTabContent;
